import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Wrench, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { BrandWord } from "@/components/BrandWord";
import { isNative } from "@/lib/native";

export default function Login() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "register" ? "register" : "login";
  const initialRole = params.get("role") === "shop" ? "shop" : "owner";
  const [mode, setMode] = useState(initialMode);
  const [role, setRole] = useState(initialRole);
  const { loginWithJWT, registerWithJWT, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", shop_name: "" });

  useEffect(() => {
    if (user) {
      if (!user.role) navigate("/onboarding");
      else if (user.role === "admin") navigate("/admin");
      else navigate(user.role === "shop" ? "/shop" : "/owner");
    }
  }, [user, navigate]);

  const handleGoogle = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // EXCEPTION: On Capacitor native, `window.location.origin` is `https://localhost`,
    // which the OAuth provider can't redirect back to on a real phone. Force the public
    // production domain so iOS Universal Links / Android App Links can deep-link the
    // callback into the app (handled by DeepLinkListener.jsx). The
    // /.well-known/assetlinks.json and apple-app-site-association files served from
    // mechanicar.app verify the app owns this domain.
    if (isNative()) {
      try {
        const { Browser } = await import("@capacitor/browser");
        const redirectUrl = "https://mechanicar.app/auth/callback";
        const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
        // Open the OAuth flow in the system browser. When the provider redirects to
        // https://mechanicar.app/auth/callback, the OS intercepts the URL (verified via
        // App Links / Universal Links) and re-opens MechaniCar with the session payload.
        // DeepLinkListener navigates to /auth/callback inside the app.
        await Browser.open({ url: authUrl, presentationStyle: "popover" });
      } catch (err) {
        toast.error("Could not open Google sign-in. Try email + password.");
      }
      return;
    }
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await loginWithJWT(form.email, form.password);
        toast.success("Welcome back");
        if (!u.role) navigate("/onboarding");
        else if (u.role === "admin") navigate("/admin");
        else navigate(u.role === "shop" ? "/shop" : "/owner");
      } else {
        const payload = {
          email: form.email,
          password: form.password,
          name: form.name,
          role,
          shop_name: role === "shop" ? form.shop_name : null,
        };
        const u = await registerWithJWT(payload);
        if (role === "shop") {
          toast.success("Application received — pending approval");
          navigate("/pending");
        } else {
          toast.success("Account created");
          navigate(u.role === "shop" ? "/shop" : "/owner");
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col safe-top safe-bottom">
      <div className="max-w-[1400px] w-full mx-auto px-4 sm:px-8 lg:px-12 py-8 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2.5" data-testid="login-home-link">
          <div className="w-8 h-8 bg-[#FF3B30] flex items-center justify-center">
            <Wrench className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <BrandWord className="font-heading text-lg font-black tracking-tight" />
        </Link>
        <Link to="/" className="btn-ghost flex items-center gap-2" data-testid="login-back-link">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md border border-white/10 bg-[#121214] p-8 sm:p-10">
          <div className="label-eyebrow mb-4">{mode === "login" ? "ACCESS THE GARAGE" : "JOIN MECHANICAR"}</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter mb-8">
            {mode === "login" ? "Sign in" : "Create account"}
          </h1>

          {mode === "register" && (
            <div className="grid grid-cols-2 gap-0 mb-6 border border-white/10">
              <button
                type="button"
                onClick={() => setRole("owner")}
                className={`px-4 py-3 text-sm uppercase tracking-widest font-medium transition-colors ${role === "owner" ? "bg-[#FF3B30] text-white" : "bg-transparent text-zinc-400 hover:bg-white/5"}`}
                data-testid="role-owner-tab"
              >
                Car Owner
              </button>
              <button
                type="button"
                onClick={() => setRole("shop")}
                className={`px-4 py-3 text-sm uppercase tracking-widest font-medium transition-colors ${role === "shop" ? "bg-[#FF3B30] text-white" : "bg-transparent text-zinc-400 hover:bg-white/5"}`}
                data-testid="role-shop-tab"
              >
                Repair Shop
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <label className="label-eyebrow block mb-2">{role === "shop" ? "OWNER NAME" : "NAME"}</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-base" data-testid="register-name-input" placeholder="Jane Doe" />
                </div>
                {role === "shop" && (
                  <div>
                    <label className="label-eyebrow block mb-2">SHOP NAME</label>
                    <input required value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })} className="input-base" data-testid="register-shop-name-input" placeholder="Main St Auto" />
                  </div>
                )}
              </>
            )}
            <div>
              <label className="label-eyebrow block mb-2">EMAIL</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value.trim() })}
                className="input-base"
                data-testid="email-input"
                placeholder="you@garage.com"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                autoComplete="email"
                inputMode="email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label-eyebrow">PASSWORD</label>
                {mode === "login" && (
                  <Link to="/forgot-password" className="text-xs text-zinc-500 hover:text-white underline underline-offset-4" data-testid="forgot-password-link">
                    Forgot?
                  </Link>
                )}
              </div>
              <div className="relative">
                <input
                  required
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="input-base pr-12"
                  data-testid="password-input"
                  placeholder="••••••••"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white p-1"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="submit-auth-btn">
              {loading ? "..." : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-zinc-600 uppercase tracking-[0.3em]">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {isNative() ? (
            <button onClick={handleGoogle} className="btn-secondary w-full flex items-center justify-center gap-3" data-testid="google-auth-btn-native">
              <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
              Continue with Google
            </button>
          ) : (
            <button onClick={handleGoogle} className="btn-secondary w-full flex items-center justify-center gap-3" data-testid="google-auth-btn">
              <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
              Continue with Google
            </button>
          )}

          <div className="mt-8 text-center text-sm text-zinc-500">
            {mode === "login" ? (
              <>New here? <button type="button" onClick={() => setMode("register")} className="text-white underline underline-offset-4" data-testid="switch-to-register">Create account</button></>
            ) : (
              <>Already have one? <button type="button" onClick={() => setMode("login")} className="text-white underline underline-offset-4" data-testid="switch-to-login">Sign in</button></>
            )}
          </div>
          <div className="mt-6 pt-6 border-t border-white/5 flex items-center justify-center gap-5 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
            <Link to="/privacy" className="hover:text-zinc-300 transition-colors" data-testid="login-privacy-link">Privacy</Link>
            <span className="text-zinc-800">·</span>
            <Link to="/terms" className="hover:text-zinc-300 transition-colors" data-testid="login-terms-link">Terms</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
